import CopyableId from "@/components/common/CopyableId";
import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PlusCircle, PhoneCall, Clock } from "lucide-react";
import PageHeader from "@/components/layout/PageHeader";
import DataTable from "@/components/common/DataTable";
import HotlineStatusBadge from "./HotlineStatusBadge";
import SlaBadge from "./SlaBadge";
import { HOTLINE_STATUS_LABELS } from "./constants";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function HotlineSessions() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const status = searchParams.get("status") || "";
  const priority = searchParams.get("priority") || "";

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(15);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const statuses = useMemo(() => (status ? status.split(",") : []), [status]);

  async function load() {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (statuses.length === 1) params.status = statuses[0];
      if (statuses.length > 1) params.statuses = statuses.join(",");
      if (priority) params.priority = priority;
      if (search) params.q = search;
      const res = await api.get("/hotline/sessions", { params });
      setItems(res.data.items);
      setTotal(res.data.total);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [page, status, priority]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { const t = setTimeout(() => { setPage(1); load(); }, 300); return () => clearTimeout(t); }, [search]);

  function setStatusFilter(v) {
    const next = new URLSearchParams(searchParams);
    if (v === "all") next.delete("status"); else next.set("status", v);
    setSearchParams(next);
  }
  function setPriorityFilter(v) {
    const next = new URLSearchParams(searchParams);
    if (v === "all") next.delete("priority"); else next.set("priority", v);
    setSearchParams(next);
  }

  const columns = [
    {
      key: "record_id", label: "Record",
      render: (r) => <span className="font-mono text-xs text-[#0B7E8C] font-medium">{r.record_id}</span>,
    },
    {
      key: "patient", label: "Patient",
      render: (r) => (
        <div className="min-w-0">
          <div className="font-medium text-[#1F2937] truncate">{r.patient?.first_name} {r.patient?.last_name}</div>
          <div className="text-xs text-[#6B7280] truncate">
            <CopyableId value={r.patient?.mrn} kind="MRN" /> · {r.patient?.patient_type === "hotline_only" ? "Hotline-only" : "H@H"}
          </div>
        </div>
      ),
    },
    {
      key: "complaint", label: "Chief Complaint",
      render: (r) => <span className="text-sm text-[#1F2937] truncate">{r.chief_complaint?.description || "—"}</span>,
    },
    {
      key: "nurse", label: "Nurse",
      render: (r) => <span className="text-xs text-[#6B7280]">{r.nurse?.first_name} {r.nurse?.last_name}</span>,
    },
    {
      key: "priority", label: "Priority",
      render: (r) => <span className="text-xs capitalize text-[#6B7280]">{r.priority || "—"}</span>,
    },
    {
      key: "status", label: "Status",
      render: (r) => <HotlineStatusBadge status={r.status} />,
    },
    {
      key: "sla", label: "SLA",
      render: (r) => <SlaBadge sla={r.sla} testId={`session-sla-${r.id}`} />,
    },
    {
      key: "updated_at", label: "Last update",
      render: (r) => (
        <div className="flex items-center gap-1 text-xs text-[#6B7280]">
          <Clock className="w-3 h-3" />
          {r.updated_at ? new Date(r.updated_at).toLocaleString() : "—"}
        </div>
      ),
    },
  ];

  return (
    <div data-testid="hotline-sessions-page">
      <PageHeader
        title="Hotline Sessions"
        subtitle={statuses.length === 0 ? "All sessions" : statuses.map((s) => HOTLINE_STATUS_LABELS[s] || s).join(" + ")}
        actions={
          <Button onClick={() => navigate("/hotline/new")} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="hotline-list-new">
            <PlusCircle className="w-4 h-4 mr-1.5" /> New Session
          </Button>
        }
      />
      <DataTable
        testId="hotline-table"
        columns={columns} data={items} total={total} page={page} pageSize={pageSize}
        loading={loading} searchValue={search} onSearchChange={setSearch} onPageChange={setPage}
        onRowClick={(r) => navigate(`/hotline/sessions/${r.id}`)}
        exportFilename="hotline-sessions.csv"
        filters={
          <>
            <Select value={statuses.length === 1 ? statuses[0] : "all"} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px] h-9 border-[#E5E7EB]" data-testid="hotline-status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {Object.entries(HOTLINE_STATUS_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={priority || "all"} onValueChange={setPriorityFilter}>
              <SelectTrigger className="w-[150px] h-9 border-[#E5E7EB]" data-testid="hotline-priority-filter">
                <SelectValue placeholder="Priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All priorities</SelectItem>
                <SelectItem value="routine">Routine</SelectItem>
                <SelectItem value="urgent">Urgent</SelectItem>
                <SelectItem value="stat">STAT</SelectItem>
              </SelectContent>
            </Select>
          </>
        }
      />
    </div>
  );
}
